import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  BarChart3, FolderTree, Cog, BookOpen, ClipboardList, FileText,
  Activity, PauseCircle, Gauge, Package, ShieldCheck, Users,
  Ship, Lock, ChevronRight, AlertTriangle, Wrench, History,
  Bug, ClipboardCheck, CalendarDays, UserCheck
} from "lucide-react";

interface PMSSidebarProps {
  activeSection: string;
  onSectionChange: (s: string) => void;
  vesselFilter: string;
  onVesselFilterChange: (v: string) => void;
  accessibleVessels: any[];
  hasFullFleetAccess: boolean;
  employeeRole: string;
  isTechnicalManager: boolean;
  overdueCount: number;
  openWOCount: number;
  criticalConditionCount: number;
  pendingDeferralCount: number;
  lowStockCount: number;
  totalEquipment: number;
  totalJobs: number;
  openDefectCount: number;
  myJobsCount?: number;
}

const NAV_SECTIONS = [
  { id: "overview", label: "Overview", group: "main" },
  { id: "my_jobs", label: "My Jobs", icon: UserCheck, group: "main" },
  { id: "dashboard", label: "Dashboard", icon: BarChart3, group: "main" },
  { id: "calendar", label: "Calendar", icon: CalendarDays, group: "main" },
  { id: "component_tree", label: "Component Tree", icon: FolderTree, group: "assets" },
  { id: "equipment", label: "Equipment Registry", icon: Cog, group: "assets" },
  { id: "job_cards", label: "Job Cards & Procedures", icon: BookOpen, group: "maintenance" },
  { id: "jobs", label: "Planned Jobs", icon: ClipboardList, group: "maintenance" },
  { id: "work_orders", label: "Work Orders", icon: FileText, group: "maintenance" },
  { id: "defects", label: "Defects & Observations", icon: Bug, group: "maintenance" },
  { id: "work_history", label: "Work History", icon: History, group: "maintenance" },
  { id: "condition_monitoring", label: "Condition Monitoring", icon: Activity, group: "maintenance" },
  { id: "running_hours", label: "Running Hours", icon: Gauge, group: "maintenance" },
  { id: "deferrals", label: "Deferrals", icon: PauseCircle, group: "approvals" },
  { id: "spare_parts", label: "Spares & Inventory", icon: Package, group: "inventory" },
  { id: "compliance", label: "Compliance & Reports", icon: ShieldCheck, group: "compliance" },
  { id: "visi_report", label: "Vessel Visit Report", icon: ClipboardCheck, group: "compliance" },
  { id: "assignments", label: "Vessel Access", icon: Users, group: "admin" },
];

const GROUP_LABELS: Record<string, string> = {
  main: "OVERVIEW",
  assets: "ASSET MANAGEMENT",
  maintenance: "MAINTENANCE",
  approvals: "APPROVALS",
  inventory: "INVENTORY & PROCUREMENT",
  compliance: "COMPLIANCE",
  admin: "ADMINISTRATION",
};

const PMSSidebar = ({
  activeSection, onSectionChange, vesselFilter, onVesselFilterChange,
  accessibleVessels, hasFullFleetAccess, employeeRole, isTechnicalManager,
  overdueCount, openWOCount, criticalConditionCount, pendingDeferralCount,
  lowStockCount, totalEquipment, totalJobs, openDefectCount, myJobsCount = 0,
}: PMSSidebarProps) => {
  const getBadge = (id: string) => {
    if (id === "my_jobs" && myJobsCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal text-white text-[9px] font-bold px-1.5">{myJobsCount}</span>;
    if (id === "jobs" && overdueCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1.5">{overdueCount}</span>;
    if (id === "work_orders" && openWOCount > 0) return <span className="ml-auto text-[9px] text-muted-foreground">{openWOCount}</span>;
    if (id === "condition_monitoring" && criticalConditionCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1.5">{criticalConditionCount}</span>;
    if (id === "deferrals" && pendingDeferralCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-500 text-white text-[9px] font-bold px-1.5">{pendingDeferralCount}</span>;
    if (id === "spare_parts" && lowStockCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 text-white text-[9px] font-bold px-1.5">{lowStockCount}</span>;
    if (id === "defects" && openDefectCount > 0) return <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1.5">{openDefectCount}</span>;
    if (id === "equipment") return <span className="ml-auto text-[9px] text-muted-foreground">{totalEquipment}</span>;
    return null;
  };

  const filteredSections = NAV_SECTIONS.filter(s => {
    if (s.id === "assignments" && !isTechnicalManager) return false;
    if (s.id === "overview") return false;
    return true;
  });

  const groups = [...new Set(filteredSections.map(s => s.group))];

  return (
    <div className="w-60 shrink-0 bg-card border-r border-border flex flex-col h-full">
      {/* Vessel Selector */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center gap-2 mb-2">
          <Ship className="w-4 h-4 text-teal" />
          <span className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase">Vessel Context</span>
        </div>
        <Select value={vesselFilter} onValueChange={onVesselFilterChange}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="All Vessels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Ship className="w-3 h-3" />
                <span>All Vessels ({accessibleVessels.length})</span>
              </div>
            </SelectItem>
            {accessibleVessels.map((v: any) => (
              <SelectItem key={v.id} value={v.id}>
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${v.status === "on_charter" || v.status === "in_transit" ? "bg-emerald-500" : v.status === "maintenance" ? "bg-red-500" : "bg-amber-500"}`} />
                  <span>{v.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!hasFullFleetAccess && (
          <div className="flex items-center gap-1.5 mt-2 text-[9px] text-blue-600">
            <Lock className="w-3 h-3" />
            <span>{employeeRole} · {accessibleVessels.length} vessel(s)</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {groups.map((group, gi) => {
            const items = filteredSections.filter(s => s.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className={gi > 0 ? "mt-4" : ""}>
                <p className="text-[9px] font-bold tracking-[2px] text-muted-foreground/60 uppercase px-2 mb-1">
                  {GROUP_LABELS[group]}
                </p>
                {items.map(item => {
                  const isActive = activeSection === item.id;
                  const Icon = item.icon!;
                  return (
                    <button
                      key={item.id}
                      onClick={() => onSectionChange(item.id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all group ${
                        isActive
                          ? "bg-teal/10 text-teal border-l-2 border-teal"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-teal" : "text-muted-foreground group-hover:text-foreground"}`} />
                      <span className="truncate">{item.label}</span>
                      {getBadge(item.id)}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Quick Stats Footer */}
      <div className="p-3 border-t border-border bg-muted/20">
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div className="flex items-center gap-1.5">
            <Cog className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">{totalEquipment} assets</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Wrench className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">{totalJobs} jobs</span>
          </div>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1.5 col-span-2">
              <AlertTriangle className="w-3 h-3 text-red-500" />
              <span className="text-red-500 font-medium">{overdueCount} overdue</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PMSSidebar;
